import { Grid, ListItemText, MenuItem, Paper, styled, TextField } from "@mui/material";
import { DateTime, MonthNumbers } from "luxon";
import { ChangeEvent, FC, useMemo, useState } from "react";
import { FinancesCurrentFutureRow, FinancesEngloboLandProject, ProjectStatuses } from "../../api/dtos";
import type { AppColumn } from "../../components/tables/AppTable.types";
import SetLinks from "../../components/layout/SetLinks";
import CollapsibleAppTable from "../../components/tables/CollapsibleAppTable";
import useFinances from "../../hooks/data/useFinances";
import useProjects from "../../hooks/data/useProjects";

const getMonthString = (month: MonthNumbers) => {
    switch (month) {
        case 1:
            return "January";
        case 2:
            return "February";
        case 3:
            return "March";
        case 4:
            return "April";
        case 5:
            return "May";
        case 6:
            return "June";
        case 7:
            return "July";
        case 8:
            return "August";
        case 9:
            return "September";
        case 10:
            return "October";
        case 11:
            return "November";
        case 12:
            return "December";
    }
};

const currencyFormatter = new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
});
const formatAUD = (value: number) => currencyFormatter.format(value);

function requiredCurrentFutureId(row: FinancesCurrentFutureRow): number {
    if (row.id === undefined || row.id === null) {
        throw new Error("Finance row is missing its ID.");
    }

    return row.id;
}

function requiredEngloboId(row: FinancesEngloboLandProject): number {
    if (row.id === undefined || row.id === null) {
        throw new Error("Englobo land project row is missing its ID.");
    }

    return row.id;
}

export function createCurrentLotsColumns(month: DateTime = DateTime.now()): AppColumn<FinancesCurrentFutureRow>[] {
    const countColumn = (
        id: "unsold" | "contractsIssued" | "contractsExchanged",
        label: string,
        width: number,
    ): AppColumn<FinancesCurrentFutureRow> => ({
        id,
        label,
        scalarType: "number",
        accessor: (row) => row[id],
        align: "left",
        width,
        aggregate: "sum",
    });
    const currencyColumn = (
        id: "month1" | "month2" | "month3" | "month4",
        label: string,
        width: number,
    ): AppColumn<FinancesCurrentFutureRow> => ({
        id,
        label,
        scalarType: "number",
        accessor: (row) => row[id],
        align: "left",
        width,
        aggregate: "sum",
        formatter: (value) => (typeof value === "number" ? formatAUD(value) : ""),
        aggregateFormatter: formatAUD,
    });
    const columns: AppColumn<FinancesCurrentFutureRow>[] = [
        {
            id: "projectName",
            label: "Project Name",
            scalarType: "text",
            accessor: (row) => row.projectName,
            width: 250,
        },
        {
            id: "stageName",
            label: "Stage Name",
            scalarType: "text",
            accessor: (row) => row.stageName,
            width: 130,
        },
        countColumn("unsold", "Unsold", 100),
        countColumn("contractsIssued", "Contracts Issued", 135),
        countColumn("contractsExchanged", "Contracts Exchanged", 160),
        {
            id: "unsoldIssuedValue",
            label: "Value of Unsold Lots & Contracts Issued",
            scalarType: "number",
            accessor: (row) => row.unsoldIssuedValue,
            align: "left",
            minWidth: 300,
            flex: 1,
            aggregate: "sum",
            formatter: (value) => (typeof value === "number" ? formatAUD(value) : ""),
            aggregateFormatter: formatAUD,
        },
        {
            id: "exchangedValue",
            label: "Sales Value of Exchanged Lots",
            scalarType: "number",
            accessor: (row) => row.exchangedValue,
            align: "left",
            width: 250,
            aggregate: "sum",
            formatter: (value) => (typeof value === "number" ? formatAUD(value) : ""),
            aggregateFormatter: formatAUD,
        },
        currencyColumn("month1", getMonthString(month.month as MonthNumbers) ?? "", 110),
        currencyColumn(
            "month2",
            getMonthString(
                month.plus({
                    months: 1,
                }).month as MonthNumbers,
            ) ?? "",
            110,
        ),
        currencyColumn(
            "month3",
            getMonthString(
                month.plus({
                    months: 2,
                }).month as MonthNumbers,
            ) ?? "",
            110,
        ),
        currencyColumn(
            "month4",
            getMonthString(
                month.plus({
                    months: 3,
                }).month as MonthNumbers,
            ) ?? "",
            110,
        ),
    ];

    return columns;
}

export function createEngloboLandProjectColumns(): AppColumn<FinancesEngloboLandProject>[] {
    const columns: AppColumn<FinancesEngloboLandProject>[] = [
        {
            id: "projectName",
            label: "Project Name",
            scalarType: "text",
            accessor: (row) => row.projectName,
            flex: 1,
        },
        {
            id: "stageName",
            label: "Stage Name",
            scalarType: "text",
            accessor: (row) => row.stageName,
            flex: 1,
        },
        {
            id: "unsold",
            label: "Unsold",
            scalarType: "number",
            accessor: (row) => row.unsold,
            aggregate: "sum",
            flex: 1,
        },
        {
            id: "assetValue",
            label: "Asset Value",
            scalarType: "number",
            accessor: (row) => row.assetValue,
            formatter: (value) => (typeof value === "number" ? formatAUD(value) : ""),
            aggregateFormatter: formatAUD,
            aggregate: "sum",
            flex: 1,
        },
    ];

    return columns;
}

const useCurrentLotsColumns = () => useMemo(() => createCurrentLotsColumns(), []);
const useEngloboLandProjectsColumns = () => useMemo(() => createEngloboLandProjectColumns(), []);

const StyledPaper = styled(Paper)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

interface IFinanceProps {}

const Finances: FC<IFinanceProps> = () => {
    const currentLotsColumns = useCurrentLotsColumns();
    const engloboLandProjectsColumns = useEngloboLandProjectsColumns();

    const { data: projects } = useProjects();

    const [selectedProjectId, setSelectedProjectId] = useState(-1);
    const [selectedProjectStatus, setSelectedProjectStatus] = useState<ProjectStatuses | -1>(-1);

    const { data: finances } = useFinances(
        selectedProjectId,
        selectedProjectStatus != -1 ? selectedProjectStatus : undefined,
    );

    const handleProjectChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSelectedProjectId(Number(event.target.value));
    };

    const handleProjectStatusChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSelectedProjectStatus(Number(event.target.value) as ProjectStatuses);
    };

    const currentLotsExport = useMemo(
        () => ({
            fileName: `${projects?.find((project) => project.id == selectedProjectId)?.name ?? (selectedProjectId == -1 ? "All Projects" : "Unknown Project")} - Current Lot Finances`,
            exportTitle: `${projects?.find((project) => project.id == selectedProjectId)?.name ?? (selectedProjectId == -1 ? "All Projects" : "Unknown Project")} - Current Lot Finances`,
            includeDateTime: true,
        }),
        [projects, selectedProjectId],
    );

    const futureLotsExport = useMemo(
        () => ({
            fileName: `${projects?.find((project) => project.id == selectedProjectId)?.name ?? (selectedProjectId == -1 ? "All Projects" : "Unknown Project")} - Future Lot Finances`,
            exportTitle: `${projects?.find((project) => project.id == selectedProjectId)?.name ?? (selectedProjectId == -1 ? "All Projects" : "Unknown Project")} - Future Lot Finances`,
            includeDateTime: true,
        }),
        [projects, selectedProjectId],
    );

    const engloboLandProjectsExport = useMemo(
        () => ({
            fileName: `${projects?.find((project) => project.id == selectedProjectId)?.name ?? (selectedProjectId == -1 ? "All Projects" : "Unknown Project")} - Current Lot Finances`,
            exportTitle: `${projects?.find((project) => project.id == selectedProjectId)?.name ?? (selectedProjectId == -1 ? "All Projects" : "Unknown Project")} - Englobo Land Project Finances`,
            includeDateTime: true,
        }),
        [projects, selectedProjectId],
    );

    return (
        <>
            <SetLinks stageLinkEnabled={false} lotLinkEnabled={false} />
            <StyledPaper
                sx={{
                    padding: 2,
                }}
            >
                <Grid container gap={2}>
                    <TextField
                        select
                        size="small"
                        label="Project"
                        value={selectedProjectId}
                        onChange={handleProjectChange}
                        slotProps={{
                            select: {
                                MenuProps: {
                                    sx: {
                                        maxHeight: 500,
                                    },
                                },
                            },
                        }}
                        sx={{
                            width: 300,
                        }}
                    >
                        <MenuItem key={-1} value={-1}>
                            <ListItemText primary={"All"} />
                        </MenuItem>
                        {projects?.map((project) => (
                            <MenuItem key={project.id} value={project.id}>
                                <ListItemText primary={project.name} />
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        size="small"
                        label="Project Status"
                        value={selectedProjectStatus}
                        onChange={handleProjectStatusChange}
                        sx={{
                            width: 200,
                        }}
                    >
                        <MenuItem key={-1} value={-1}>
                            <ListItemText primary={"All"} />
                        </MenuItem>
                        <MenuItem key={ProjectStatuses.ReleasedToMarket} value={ProjectStatuses.ReleasedToMarket}>
                            <ListItemText primary={"Released To Market"} />
                        </MenuItem>
                        <MenuItem key={ProjectStatuses.Pipeline} value={ProjectStatuses.Pipeline}>
                            <ListItemText primary={"Pipeline"} />
                        </MenuItem>
                        <MenuItem key={ProjectStatuses.Archived} value={ProjectStatuses.Archived}>
                            <ListItemText primary={"Archived"} />
                        </MenuItem>
                    </TextField>
                </Grid>
            </StyledPaper>
            <StyledPaper>
                <CollapsibleAppTable
                    title="Current Lots"
                    density="compact"
                    loading={false}
                    columns={currentLotsColumns}
                    rows={finances?.currentLots ?? []}
                    getRowId={requiredCurrentFutureId}
                    startCollapsed
                    columnControls={{
                        menu: true,
                    }}
                    filterControls
                    export={currentLotsExport}
                />
            </StyledPaper>
            <StyledPaper>
                <CollapsibleAppTable
                    title="Future Lots"
                    density="compact"
                    loading={false}
                    columns={currentLotsColumns}
                    rows={finances?.futureLots ?? []}
                    getRowId={requiredCurrentFutureId}
                    startCollapsed
                    columnControls={{
                        menu: true,
                    }}
                    filterControls
                    export={futureLotsExport}
                />
            </StyledPaper>
            <StyledPaper>
                <CollapsibleAppTable
                    title="Englobo Land Projects"
                    density="compact"
                    loading={false}
                    columns={engloboLandProjectsColumns}
                    rows={finances?.engloboLandProjects ?? []}
                    getRowId={requiredEngloboId}
                    startCollapsed
                    columnControls={{
                        menu: true,
                    }}
                    filterControls
                    export={engloboLandProjectsExport}
                />
            </StyledPaper>
        </>
    );
};

export default Finances;
